import * as THREE from 'three';
import { Model } from '../core/model';
import { triangulateFace } from '../core/face';
import { SelectionState } from '../app/selection';
import { Vec3 } from '../core/vec';

/**
 * Baut das Modell als Three.js-Objekte: eine Mesh pro Fläche (für Picking/Farben),
 * schwarze Kanten als LineSegments, Hilfslinien gestrichelt, Auswahl blau.
 * update() nach jeder Modelländerung.
 */
export class ModelRenderer {
  group = new THREE.Group();
  vanGroup = new THREE.Group();
  /** Vorschau-Objekte der Werkzeuge (Rubber-Band-Linien etc.). */
  previewGroup = new THREE.Group();

  private faceMeshes = new Map<number, THREE.Mesh>();
  private edges?: THREE.LineSegments;
  private guides?: THREE.LineSegments;
  private groupBoxes: THREE.LineSegments[] = [];

  static DEFAULT_FACE_COLOR = '#ffffff';
  static SELECTED_FACE_COLOR = '#b8d2f5';
  static SELECTED_EDGE_COLOR = 0x2f6fe0;
  static HOVER_FACE_COLOR = '#ffe9a8';
  static EDGE_COLOR = 0x212121;
  static GUIDE_COLOR = 0x9a68d8;
  /** Geometrie außerhalb der geöffneten Gruppe wird ausgegraut (wie SketchUp). */
  static FADED_EDGE_COLOR = 0xbdbdbd;
  static CONTEXT_BOX_COLOR = 0x808080;

  hoverFaceId: number | null = null;

  constructor(
    private model: Model,
    private selection: SelectionState
  ) {
    this.group.add(this.previewGroup);
    this.group.add(this.vanGroup);
  }

  update(): void {
    // Flächen
    const seen = new Set<number>();
    for (const face of this.model.faces.values()) {
      // Ausgeblendete Ebenen: kein Mesh (damit auch nicht wählbar)
      if (!this.model.isEntityVisible(face.group)) continue;
      seen.add(face.id);
      // Gewählte geschlossene Gruppe: nur Rahmen, keine blauen Flächen
      const selected = this.selection.faces.has(face.id) && face.group === this.model.editContext;
      const hovered = this.hoverFaceId === face.id;
      const color =
        selected ? ModelRenderer.SELECTED_FACE_COLOR : hovered ? ModelRenderer.HOVER_FACE_COLOR : face.color ?? ModelRenderer.DEFAULT_FACE_COLOR;
      let mesh = this.faceMeshes.get(face.id);
      const outer = face.outer.map((id) => this.model.vertexPosition(id));
      const holes = face.holes.map((h) => h.map((id) => this.model.vertexPosition(id)));
      const { triangles } = triangulateFace(outer, holes);
      const positions = new Float32Array(triangles.length * 3);
      for (let i = 0; i < triangles.length; i++) {
        const p = triangles[i]!;
        positions[i * 3] = p.x;
        positions[i * 3 + 1] = p.y;
        positions[i * 3 + 2] = p.z;
      }
      if (!mesh) {
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        const mat = new THREE.MeshLambertMaterial({ color, side: THREE.DoubleSide });
        mesh = new THREE.Mesh(geo, mat);
        mesh.userData.faceId = face.id;
        this.group.add(mesh);
        this.faceMeshes.set(face.id, mesh);
      } else {
        const attr = mesh.geometry.getAttribute('position') as THREE.BufferAttribute;
        if (attr.count * 3 !== positions.length) {
          mesh.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        } else {
          attr.copyArray(positions);
          attr.needsUpdate = true;
        }
        mesh.geometry.computeBoundingSphere();
        (mesh.material as THREE.MeshLambertMaterial).color.set(color);
      }
      const mat = mesh.material as THREE.MeshLambertMaterial;
      // Außerhalb der geöffneten Gruppe ausgegraut (0.3) × Ebenen-Deckkraft
      const faded = !this.model.isInside(face.group, this.model.editContext);
      const opacity = (faded ? 0.3 : 1) * this.model.entityOpacity(face.group);
      const transparent = opacity < 0.999;
      if (mat.transparent !== transparent || mat.opacity !== opacity) {
        mat.transparent = transparent;
        mat.opacity = opacity;
        mat.depthWrite = !transparent;
        mat.needsUpdate = true;
      }
    }
    for (const [id, mesh] of this.faceMeshes) {
      if (!seen.has(id)) {
        this.group.remove(mesh);
        mesh.geometry.dispose();
        (mesh.material as THREE.Material).dispose();
        this.faceMeshes.delete(id);
      }
    }

    // Kanten (mit Alpha-Kanal: Deckkraft je Kante über Vertex-Farbe)
    const edgePts: number[] = [];
    const edgeColors: number[] = [];
    const cSel = new THREE.Color(ModelRenderer.SELECTED_EDGE_COLOR);
    const cEdge = new THREE.Color(ModelRenderer.EDGE_COLOR);
    const cFaded = new THREE.Color(ModelRenderer.FADED_EDGE_COLOR);
    const ctx = this.model.editContext;
    for (const e of this.model.edges.values()) {
      if (!this.model.isEntityVisible(e.group)) continue;
      const a = this.model.vertexPosition(e.a);
      const b = this.model.vertexPosition(e.b);
      // Gewählte geschlossene Gruppen zeigen nur den blauen Rahmen, Kanten bleiben schwarz
      const c =
        this.selection.edges.has(e.id) && e.group === ctx ? cSel :
        !this.model.isInside(e.group, ctx) ? cFaded : cEdge;
      const alpha = (this.model.isInside(e.group, ctx) ? 1 : 0.3) * this.model.entityOpacity(e.group);
      edgePts.push(a.x, a.y, a.z, b.x, b.y, b.z);
      edgeColors.push(c.r, c.g, c.b, alpha, c.r, c.g, c.b, alpha);
    }
    if (this.edges) {
      this.group.remove(this.edges);
      this.edges.geometry.dispose();
      (this.edges.material as THREE.Material).dispose();
    }
    const egeo = new THREE.BufferGeometry();
    egeo.setAttribute('position', new THREE.Float32BufferAttribute(edgePts, 3));
    egeo.setAttribute('color', new THREE.Float32BufferAttribute(edgeColors, 4));
    this.edges = new THREE.LineSegments(egeo, new THREE.LineBasicMaterial({ vertexColors: true, transparent: true }));
    this.edges.renderOrder = 2;
    this.group.add(this.edges);

    this.updateGroupBoxes();

    // Hilfslinien
    const guidePts: number[] = [];
    for (const g of this.model.guides.values()) {
      if (g.infinite) {
        const dir = new THREE.Vector3(g.b.x - g.a.x, g.b.y - g.a.y, g.b.z - g.a.z).normalize();
        guidePts.push(
          g.a.x - dir.x * 200, g.a.y - dir.y * 200, g.a.z - dir.z * 200,
          g.a.x + dir.x * 200, g.a.y + dir.y * 200, g.a.z + dir.z * 200
        );
      } else {
        guidePts.push(g.a.x, g.a.y, g.a.z, g.b.x, g.b.y, g.b.z);
      }
    }
    if (this.guides) {
      this.group.remove(this.guides);
      this.guides.geometry.dispose();
    }
    if (guidePts.length > 0) {
      const ggeo = new THREE.BufferGeometry();
      ggeo.setAttribute('position', new THREE.Float32BufferAttribute(guidePts, 3));
      this.guides = new THREE.LineSegments(ggeo, new THREE.LineDashedMaterial({ color: ModelRenderer.GUIDE_COLOR, dashSize: 0.12, gapSize: 0.08 }));
      this.guides.computeLineDistances();
      this.guides.renderOrder = 3;
      this.group.add(this.guides);
    } else {
      this.guides = undefined;
    }
  }

  /** Umriss-Quader in Gruppenachsen: blau für gewählte geschlossene Gruppen, grau gestrichelt für die geöffnete. */
  private updateGroupBoxes(): void {
    for (const obj of this.groupBoxes) {
      this.group.remove(obj);
      obj.geometry.dispose();
      (obj.material as THREE.Material).dispose();
    }
    this.groupBoxes = [];
    const selected = this.model.groupsAt(this.selection.edges, this.selection.faces);
    const selPts = [...selected].flatMap((gid) => this.boxSegments(gid));
    if (selPts.length > 0) {
      this.addBoxLines(selPts, new THREE.LineBasicMaterial({ color: ModelRenderer.SELECTED_EDGE_COLOR }), false);
    }
    const ctx = this.model.editContext;
    if (ctx !== undefined) {
      const ctxPts = this.boxSegments(ctx);
      if (ctxPts.length > 0) {
        this.addBoxLines(ctxPts, new THREE.LineDashedMaterial({ color: ModelRenderer.CONTEXT_BOX_COLOR, dashSize: 0.05, gapSize: 0.04 }), true);
      }
    }
  }

  private addBoxLines(pts: number[], mat: THREE.Material, dashed: boolean): void {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
    const lines = new THREE.LineSegments(geo, mat);
    if (dashed) lines.computeLineDistances();
    lines.renderOrder = 3;
    this.group.add(lines);
    this.groupBoxes.push(lines);
  }

  /** 12 Kanten des Umriss-Quaders einer Gruppe, ausgerichtet an ihren Achsen. */
  private boxSegments(gid: number): number[] {
    const { edges, faces } = this.model.entitiesInGroup(gid);
    const vids = this.model.verticesOf(edges, faces);
    if (vids.size === 0) return [];
    const axes = this.model.groupAxes(gid);
    const min = [Infinity, Infinity, Infinity];
    const max = [-Infinity, -Infinity, -Infinity];
    for (const id of vids) {
      const p = this.model.vertexPosition(id);
      for (let i = 0; i < 3; i++) {
        const a = axes[i]!;
        const d = p.x * a.x + p.y * a.y + p.z * a.z;
        min[i] = Math.min(min[i]!, d);
        max[i] = Math.max(max[i]!, d);
      }
    }
    // Minimal aufblähen, damit der Rahmen nicht mit den Kanten z-kämpft
    for (let i = 0; i < 3; i++) { min[i]! -= 0.004; max[i]! += 0.004; }
    const corner = (bx: number, by: number, bz: number): number[] => {
      const c = [bx ? max[0]! : min[0]!, by ? max[1]! : min[1]!, bz ? max[2]! : min[2]!];
      return [0, 1, 2].map((k) => axes[0]![(['x', 'y', 'z'] as const)[k]!] * c[0]! + axes[1]![(['x', 'y', 'z'] as const)[k]!] * c[1]! + axes[2]![(['x', 'y', 'z'] as const)[k]!] * c[2]!);
    };
    const cs = [0, 1, 2, 3, 4, 5, 6, 7].map((i) => corner(i & 1 ? 1 : 0, i & 2 ? 1 : 0, i & 4 ? 1 : 0));
    const idx = [0, 1, 1, 3, 3, 2, 2, 0, 4, 5, 5, 7, 7, 6, 6, 4, 0, 4, 1, 5, 2, 6, 3, 7];
    return idx.flatMap((i) => cs[i]!);
  }

  /** Raycast auf Flächen (für Auswahl / Push/Pull / Farben). */
  pickFace(ndcX: number, ndcY: number, camera: THREE.PerspectiveCamera): number | null {
    const rc = new THREE.Raycaster();
    rc.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);
    const meshes = [...this.faceMeshes.values()];
    const hits = rc.intersectObjects(meshes, false);
    for (const h of hits) {
      const id = h.object.userData.faceId as number;
      if (typeof id === 'number') return id;
    }
    return null;
  }

  /** Vorderste Fläche unter dem Cursor inkl. Trefferpunkt und Abstand entlang des Strahls. */
  pickFaceHit(ndcX: number, ndcY: number, camera: THREE.PerspectiveCamera): { faceId: number; point: Vec3; distance: number } | null {
    const rc = new THREE.Raycaster();
    rc.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);
    for (const h of rc.intersectObjects([...this.faceMeshes.values()], false)) {
      const id = h.object.userData.faceId as number;
      if (typeof id === 'number') return { faceId: id, point: { x: h.point.x, y: h.point.y, z: h.point.z }, distance: h.distance };
    }
    return null;
  }

  /** Wie pickFace, aber nur Flächen des aktuellen Bearbeitungskontexts (geschlossene Gruppen sind gesperrt). */
  pickEditableFace(ndcX: number, ndcY: number, camera: THREE.PerspectiveCamera): number | null {
    const id = this.pickFace(ndcX, ndcY, camera);
    return id !== null && this.model.isEditable(this.model.faces.get(id)) ? id : null;
  }

  /** Raycast-Treffpunkt auf der nächsten Fläche (Drehpunkt/Zoom-Ziel der Kamera). */
  pickPoint(ndcX: number, ndcY: number, camera: THREE.PerspectiveCamera): Vec3 | null {
    const rc = new THREE.Raycaster();
    rc.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);
    const hit = rc.intersectObjects([...this.faceMeshes.values()], false)[0];
    return hit ? { x: hit.point.x, y: hit.point.y, z: hit.point.z } : null;
  }

  /** Alle Modellpunkte — für Zoom Extents. */
  allPoints(): Vec3[] {
    return [...this.model.vertices.values()].map((v) => v.p);
  }
}
